import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, DollarSign, TrendingUp, Wallet, Users, Package, ShoppingBag,
  Percent, Warehouse, Target, Gauge, Save, Trash2, Layers, RefreshCw,
  Loader2, ChevronDown, ChevronUp, Zap, BarChart3, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

/**
 * AdminIntelligence — Executive Intelligence Center.
 * P2-A MVP: Executive Dashboard · Financial Simulator · Cash Flow · 5-Year
 * Forecast · Break-even · Scenario Manager · Scenario Comparison.
 * Remaining submodules (Compensation/Inventory/Marketing simulators,
 * Sensitivity Analysis, AI CFO, Investor Presentation, Export Center) are
 * scaffolded as tabs but marked "Phase 2" so admins see the roadmap.
 *
 * All math flows through the pure backend engine (`/api/intel/compute`).
 * The UI is a controlled form — every assumption edit debounces a recompute
 * so KPIs live-update.
 */

const TABS = [
  { key: "dashboard", label: "Executive Dashboard", icon: BarChart3 },
  { key: "simulator", label: "Financial Simulator", icon: Activity },
  { key: "forecast", label: "5-Year Forecast", icon: TrendingUp },
  { key: "cashflow", label: "Cash Flow", icon: Wallet },
  { key: "profitability", label: "Break-even & Profitability", icon: Target },
  { key: "scenarios", label: "Scenario Manager", icon: Layers },
  { key: "comparison", label: "Scenario Comparison", icon: Gauge },
  { key: "roadmap", label: "Roadmap (Phase 2)", icon: Zap },
];

const PESO = (n) =>
  `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const PESO_FINE = (n) =>
  `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PCT = (n) => `${Number(n || 0).toFixed(2)}%`;

const HEALTH_COLOR = {
  green: { bg: "bg-emerald-500/12", text: "text-emerald-300", border: "border-emerald-400/40", dot: "bg-emerald-400" },
  yellow: { bg: "bg-[#FBBF24]/12", text: "text-[#FBBF24]", border: "border-[#FBBF24]/40", dot: "bg-[#FBBF24]" },
  red: { bg: "bg-[#DC2626]/12", text: "text-[#FCA5A5]", border: "border-[#DC2626]/40", dot: "bg-[#DC2626]" },
};

// Grouping of assumptions for the Simulator form — matches the roadmap spec.
const ASSUMPTION_GROUPS = [
  {
    label: "Product Economics", icon: Package,
    fields: [
      ["manufacturing_cost", "Manufacturing Cost (₱/unit)"],
      ["landed_cost", "Landed Cost (shipping/customs)"],
      ["packaging_cost", "Packaging Cost"],
      ["warehouse_cost_per_unit", "Warehouse Cost (per unit)"],
      ["returns_pct", "Returns %"],
      ["damage_pct", "Damage %"],
      ["payment_gateway_pct", "Payment Gateway %"],
    ],
  },
  {
    label: "Pricing", icon: DollarSign,
    fields: [
      ["srp", "SRP (Suggested Retail Price)"],
      ["member_price_starter", "Member Price — Starter"],
      ["member_price_builder", "Member Price — Builder"],
      ["member_price_executive", "Member Price — Executive"],
      ["wholesale_price", "Wholesale Price"],
    ],
  },
  {
    label: "Business Starter Kits", icon: ShoppingBag,
    fields: [
      ["starter_kit_price", "Starter Kit Price"],
      ["builder_kit_price", "Builder Kit Price"],
      ["executive_kit_price", "Executive Kit Price"],
      ["starter_products_included", "Products in Starter Kit"],
      ["builder_products_included", "Products in Builder Kit"],
      ["executive_products_included", "Products in Executive Kit"],
      ["kit_mix_starter_pct", "Kit Mix — Starter %"],
      ["kit_mix_builder_pct", "Kit Mix — Builder %"],
      ["kit_mix_executive_pct", "Kit Mix — Executive %"],
    ],
  },
  {
    label: "Sales & Growth", icon: TrendingUp,
    fields: [
      ["monthly_new_members", "Monthly New Members"],
      ["monthly_active_members_pct", "Monthly Active Members %"],
      ["monthly_orders_per_active_member", "Orders / Active Member"],
      ["average_basket_size", "Average Basket (units/order)"],
      ["repeat_purchase_pct", "Repeat Purchase %"],
      ["monthly_sales_growth_pct", "Monthly Sales Growth %"],
      ["customer_churn_pct", "Customer Churn %"],
      ["retention_rate_pct", "Retention Rate %"],
      ["customer_lifetime_months", "Customer Lifetime (months)"],
      ["conversion_rate_pct", "Conversion Rate %"],
      ["monthly_traffic", "Monthly Traffic (visitors)"],
      ["cac_php", "CAC (₱)"],
    ],
  },
  {
    label: "Commission Engine", icon: Percent,
    fields: [
      ["retail_margin_starter_pct", "Retail Margin — Starter %"],
      ["retail_margin_builder_pct", "Retail Margin — Builder %"],
      ["retail_margin_executive_pct", "Retail Margin — Executive %"],
      ["direct_referral_pct", "Direct Referral % (of kit)"],
      ["level_1_pct", "Team Override L1 %"],
      ["level_2_pct", "Team Override L2 %"],
      ["level_3_pct", "Team Override L3 %"],
      ["level_4_pct", "Team Override L4 %"],
      ["level_5_pct", "Team Override L5 %"],
      ["leadership_bonus_pct", "Leadership Bonus %"],
      ["matching_bonus_pct", "Matching Bonus %"],
      ["global_bonus_pool_pct", "Global Bonus Pool %"],
      ["max_commission_cap_pct", "Max Commission Cap %"],
    ],
  },
  {
    label: "Operating Expenses (monthly ₱)", icon: Warehouse,
    fields: [
      ["opex_warehouse", "Warehouse"],
      ["opex_payroll", "Payroll"],
      ["opex_software", "Software"],
      ["opex_hosting", "Hosting"],
      ["opex_ai", "AI / LLM"],
      ["opex_crm", "CRM"],
      ["opex_customer_support", "Customer Support"],
      ["opex_legal", "Legal"],
      ["opex_accounting", "Accounting"],
      ["opex_marketing", "Marketing"],
      ["opex_office", "Office"],
      ["opex_utilities", "Utilities"],
      ["opex_insurance", "Insurance"],
      ["opex_transportation", "Transportation"],
      ["opex_misc", "Miscellaneous"],
    ],
  },
  {
    label: "Capital & Tax", icon: Wallet,
    fields: [
      ["opening_cash", "Opening Cash (₱)"],
      ["tax_rate_pct", "Corporate Tax Rate %"],
      ["warehouse_capacity_units", "Warehouse Capacity (units)"],
      ["valuation_multiple", "Valuation Multiple (× Rev)"],
    ],
  },
];

export default function AdminIntelligence() {
  const [tab, setTab] = useState("dashboard");
  const [assumptions, setAssumptions] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);

  const [scenarios, setScenarios] = useState([]);
  const [selectedCompareIds, setSelectedCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);

  // Load defaults + scenarios once.
  useEffect(() => {
    (async () => {
      try {
        const [d, s] = await Promise.all([
          api.get("/intel/defaults"),
          api.get("/intel/scenarios"),
        ]);
        setAssumptions(d.data.assumptions);
        setScenarios(s.data || []);
      } catch (e) { toast.error(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  // Debounced recompute — the assumption bundle is ~70 keys, so we don't want
  // to fire on every keystroke.
  useEffect(() => {
    if (!assumptions) return;
    const t = setTimeout(async () => {
      setComputing(true);
      try {
        const { data } = await api.post("/intel/compute", { assumptions });
        setSnapshot(data);
      } catch (e) { toast.error(formatErr(e)); }
      finally { setComputing(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [assumptions]);

  const updateField = (key, value) => {
    setAssumptions((prev) => ({ ...prev, [key]: value === "" ? 0 : Number(value) || 0 }));
  };

  const reloadScenarios = async () => {
    const { data } = await api.get("/intel/scenarios");
    setScenarios(data || []);
  };

  const saveScenario = async () => {
    const name = window.prompt("Scenario name? (e.g. Conservative Q3)");
    if (!name) return;
    const tag = window.prompt("Tag? default/conservative/expected/aggressive/hyper_growth/custom", "custom") || "custom";
    try {
      await api.post("/intel/scenarios", { name, description: "", tag, assumptions });
      toast.success(`Saved scenario "${name}".`);
      await reloadScenarios();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadScenario = async (sid) => {
    try {
      const { data } = await api.get(`/intel/scenarios/${sid}`);
      setAssumptions({ ...data.assumptions });
      toast.success(`Loaded "${data.name}".`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteScenario = async (sid) => {
    if (!window.confirm("Delete this scenario? This cannot be undone.")) return;
    try {
      await api.delete(`/intel/scenarios/${sid}`);
      toast.success("Scenario deleted.");
      await reloadScenarios();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const runCompare = async () => {
    if (selectedCompareIds.length < 2) {
      toast.error("Pick at least 2 scenarios to compare.");
      return;
    }
    try {
      const { data } = await api.post("/intel/scenarios/compare", { scenario_ids: selectedCompareIds });
      setCompareData(data.scenarios || []);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading || !assumptions) {
    return <div className="glass-card p-10 text-white/60 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading Executive Intelligence…</div>;
  }

  return (
    <div className="space-y-6" data-testid="intel-root">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Executive Intelligence Center</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Financial Simulation & Investor Command</h1>
          <p className="text-white/60 mt-1 text-sm max-w-2xl">Live-computed KPIs, cash flow, forecasts, and break-even — driven by an editable assumption bundle.</p>
        </div>
        <div className="flex items-center gap-2">
          {computing && <span className="text-[10px] uppercase tracking-widest text-[#FBBF24] inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Recomputing</span>}
          <button
            onClick={saveScenario}
            className="text-xs px-3 py-2 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1"
            data-testid="intel-save-scenario"
          >
            <Save size={12} /> Save as Scenario
          </button>
        </div>
      </div>

      {/* Tab strip */}
      <div className="glass-card p-1 inline-flex rounded-lg gap-1 flex-wrap" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 rounded-md text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 ${
              tab === t.key ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"
            }`}
            data-testid={`intel-tab-${t.key}`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {snapshot && tab === "dashboard" && <ExecutiveDashboard snap={snapshot} />}
      {snapshot && tab === "simulator" && <Simulator assumptions={assumptions} updateField={updateField} snap={snapshot} />}
      {snapshot && tab === "forecast" && <Forecast5y snap={snapshot} />}
      {snapshot && tab === "cashflow" && <CashFlow snap={snapshot} />}
      {snapshot && tab === "profitability" && <Profitability snap={snapshot} />}
      {tab === "scenarios" && (
        <ScenarioManager
          scenarios={scenarios}
          onLoad={loadScenario}
          onDelete={deleteScenario}
          onSave={saveScenario}
          onRefresh={reloadScenarios}
        />
      )}
      {tab === "comparison" && (
        <ScenarioCompare
          scenarios={scenarios}
          selectedIds={selectedCompareIds}
          setSelectedIds={setSelectedCompareIds}
          data={compareData}
          onRun={runCompare}
        />
      )}
      {tab === "roadmap" && <Roadmap />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Executive Dashboard                                                 */
/* ------------------------------------------------------------------ */
function ExecutiveDashboard({ snap }) {
  const cards = [
    { label: "Monthly Revenue", value: PESO(snap.monthly_revenue), icon: DollarSign, accent: "gold" },
    { label: "Monthly Net Profit", value: PESO(snap.monthly_net_profit), icon: TrendingUp, accent: "emerald" },
    { label: "Monthly EBITDA", value: PESO(snap.monthly_ebitda), icon: Activity, accent: "blue" },
    { label: "Cash Position (12mo)", value: PESO(snap.cash_position_12mo), icon: Wallet, accent: "gold" },
    { label: "Working Capital", value: PESO(snap.working_capital), icon: Gauge, accent: "white" },
    { label: "GMV (monthly)", value: PESO(snap.monthly_gmv), icon: ShoppingBag, accent: "blue" },
    { label: "Active Members", value: snap.active_members.toLocaleString(), icon: Users, accent: "gold" },
    { label: "Retail Customers", value: snap.retail_customers.toLocaleString(), icon: Users, accent: "blue" },
    { label: "Warehouse Value", value: PESO(snap.warehouse_value), icon: Warehouse, accent: "white" },
    { label: "Affiliate Payout", value: PESO(snap.monthly_commission), icon: Users, accent: "red" },
    { label: "Operating Margin", value: PCT(snap.operating_margin_pct), icon: Percent, accent: "gold" },
    { label: "Net Margin", value: PCT(snap.net_margin_pct), icon: TrendingUp, accent: "emerald" },
  ];
  return (
    <div className="space-y-6" data-testid="intel-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="intel-kpi-grid">
        {cards.map((c) => (
          <div key={c.label} className="glass-card p-4" data-testid={`kpi-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.16em] text-white/50 font-semibold">
              <c.icon size={11} /> {c.label}
            </div>
            <div className={`mt-2 font-mono-num text-lg font-bold ${
              c.accent === "gold" ? "text-[#FBBF24]" :
              c.accent === "emerald" ? "text-emerald-300" :
              c.accent === "blue" ? "text-[#93C5FD]" :
              c.accent === "red" ? "text-[#FCA5A5]" :
              "text-white"
            }`}>{c.value}</div>
          </div>
        ))}
      </div>

      <KpiHealthBoard snap={snap} />
    </div>
  );
}

function KpiHealthBoard({ snap }) {
  const rows = [
    { key: "gross_margin", label: "Gross Margin", value: PCT(snap.gross_margin_pct), target: "≥55% green · 45-54% yellow" },
    { key: "net_profit_margin", label: "Net Profit Margin", value: PCT(snap.net_margin_pct), target: "≥18% green · 10-17% yellow" },
    { key: "commission_ratio", label: "Commission Ratio", value: PCT(snap.commission_ratio_pct), target: "≤25% green · 26-30% yellow" },
    { key: "active_partner_rate", label: "Active Partner Rate", value: PCT(snap.assumptions_used?.monthly_active_members_pct || 0), target: "≥70% green · 60-69% yellow" },
    { key: "customer_retention", label: "Customer Retention", value: PCT(snap.assumptions_used?.retention_rate_pct || 0), target: "≥75% green · 65-74% yellow" },
    { key: "ltv_cac_ratio", label: "LTV : CAC", value: `${snap.ltv_cac_ratio.toFixed(2)}x`, target: "≥3× green · 2-2.9× yellow" },
    { key: "cash_reserve_months", label: "Runway (months)", value: snap.runway_months >= 999 ? "∞" : snap.runway_months.toFixed(1), target: "≥6mo green · 3-5mo yellow" },
    { key: "inventory_turnover", label: "Inventory Turnover", value: "6.5×", target: "Placeholder — needs live inventory" },
  ];
  return (
    <div className="glass-card p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">KPI Health vs Executive Targets</div>
      <div className="grid gap-2 md:grid-cols-2">
        {rows.map((r) => {
          const color = HEALTH_COLOR[snap.kpi_health[r.key]] || HEALTH_COLOR.yellow;
          return (
            <div key={r.key} className={`flex items-center justify-between px-3 py-2 rounded-md border ${color.border} ${color.bg}`} data-testid={`health-${r.key}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${color.dot}`} />
                <div>
                  <div className="text-sm font-bold text-white">{r.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">{r.target}</div>
                </div>
              </div>
              <div className={`font-mono-num text-lg font-bold ${color.text}`}>{r.value}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Financial Simulator                                                 */
/* ------------------------------------------------------------------ */
function Simulator({ assumptions, updateField, snap }) {
  const [openGroups, setOpenGroups] = useState({ "Product Economics": true, "Pricing": true });
  const toggle = (label) => setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]" data-testid="intel-simulator">
      <div className="space-y-3">
        {ASSUMPTION_GROUPS.map((group) => {
          const open = openGroups[group.label] ?? false;
          return (
            <div key={group.label} className="glass-card overflow-hidden">
              <button
                onClick={() => toggle(group.label)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.03]"
                data-testid={`sim-group-${group.label.replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-2">
                  <group.icon size={16} className="text-[#FBBF24]" />
                  <span className="font-display font-black text-sm">{group.label}</span>
                </div>
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {open && (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 p-4 pt-0 border-t border-white/5">
                  {group.fields.map(([key, label]) => (
                    <div key={key}>
                      <label className="text-[10px] uppercase tracking-[0.16em] text-white/45 font-bold">{label}</label>
                      <input
                        type="number"
                        step="0.01"
                        className="zfd-input mt-1"
                        value={assumptions[key] ?? 0}
                        onChange={(e) => updateField(key, e.target.value)}
                        data-testid={`sim-field-${key}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Live KPI mirror */}
      <div className="space-y-3 sticky top-4 h-fit">
        <div className="glass-card gold-border p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">Live KPIs</div>
          <MiniStat label="Monthly Revenue" value={PESO(snap.monthly_revenue)} />
          <MiniStat label="Gross Profit" value={PESO(snap.monthly_gross_profit)} sub={PCT(snap.gross_margin_pct)} />
          <MiniStat label="EBITDA" value={PESO(snap.monthly_ebitda)} sub={PCT(snap.operating_margin_pct)} />
          <MiniStat label="Net Profit" value={PESO(snap.monthly_net_profit)} sub={PCT(snap.net_margin_pct)} />
          <MiniStat label="Commission Ratio" value={PCT(snap.commission_ratio_pct)} />
          <MiniStat label="Cash (12mo)" value={PESO(snap.cash_position_12mo)} />
          <MiniStat label="Break-even Revenue" value={PESO(snap.break_even_revenue)} sub={`${snap.break_even_units.toLocaleString()} units`} />
          <MiniStat label="Runway" value={snap.runway_months >= 999 ? "Infinite" : `${snap.runway_months} months`} />
          <MiniStat label="LTV : CAC" value={`${snap.ltv_cac_ratio.toFixed(2)}×`} />
        </div>
        <div className="glass-card p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-2">Per-Unit Economics</div>
          <MiniStat label="Effective Unit Cost" value={PESO_FINE(snap.effective_unit_cost)} />
          <MiniStat label="Unit Gross Margin" value={PESO_FINE(snap.unit_gross_margin)} />
          <MiniStat label="Profit / Product (after commission)" value={PESO_FINE(snap.profit_per_product)} />
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-white/5 last:border-0">
      <div className="text-[11px] text-white/60">{label}</div>
      <div className="text-right">
        <div className="font-mono-num text-sm font-bold text-[#FBBF24]">{value}</div>
        {sub && <div className="text-[10px] text-white/40">{sub}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 5-Year Forecast                                                     */
/* ------------------------------------------------------------------ */
function Forecast5y({ snap }) {
  return (
    <div className="glass-card p-0 overflow-hidden" data-testid="intel-forecast">
      <div className="px-5 py-4 border-b border-white/10">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold">5-Year Forecast</div>
        <div className="text-sm text-white/60">Annual roll-up with compounding growth from Month 1 base.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-[10px] uppercase tracking-[0.18em] text-white/50">
            <tr>
              <th className="text-left px-4 py-2.5">Year</th>
              <th className="text-right px-4 py-2.5">Revenue</th>
              <th className="text-right px-4 py-2.5">Gross Profit</th>
              <th className="text-right px-4 py-2.5">Commission</th>
              <th className="text-right px-4 py-2.5">Opex</th>
              <th className="text-right px-4 py-2.5">EBITDA</th>
              <th className="text-right px-4 py-2.5">Net Profit</th>
              <th className="text-right px-4 py-2.5">Cash Position</th>
              <th className="text-right px-4 py-2.5">Members</th>
              <th className="text-right px-4 py-2.5">Valuation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {snap.forecast_5y.map((y) => (
              <tr key={y.year} className="hover:bg-white/[0.02]" data-testid={`forecast-year-${y.year}`}>
                <td className="px-4 py-2.5 font-display font-black text-[#FBBF24]">Y{y.year}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-white">{PESO(y.revenue)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#93C5FD]">{PESO(y.gross_profit)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#FCA5A5]">{PESO(y.commission)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-white/70">{PESO(y.opex)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#FBBF24]">{PESO(y.ebitda)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-emerald-300 font-bold">{PESO(y.net_profit)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-white">{PESO(y.cash_position)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-white/80">{y.members.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#FBBF24] font-bold">{PESO(y.valuation)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Cash Flow (60 months)                                               */
/* ------------------------------------------------------------------ */
function CashFlow({ snap }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? snap.cash_flow_monthly : snap.cash_flow_monthly.slice(0, 24);
  return (
    <div className="glass-card p-0 overflow-hidden" data-testid="intel-cashflow">
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold">Monthly Cash Flow Projection</div>
          <div className="text-sm text-white/60">Showing {rows.length} of {snap.cash_flow_monthly.length} months.</div>
        </div>
        <button onClick={() => setShowAll(!showAll)} className="text-xs px-3 py-1.5 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24]" data-testid="intel-cf-toggle">
          {showAll ? "Show first 24 months" : `Show all ${snap.cash_flow_monthly.length} months`}
        </button>
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-xs">
          <thead className="bg-black/40 text-[10px] uppercase tracking-[0.16em] text-white/50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Month</th>
              <th className="text-right px-3 py-2">Opening Cash</th>
              <th className="text-right px-3 py-2">Kit Sales</th>
              <th className="text-right px-3 py-2">Product Sales</th>
              <th className="text-right px-3 py-2">COGS</th>
              <th className="text-right px-3 py-2">Commission</th>
              <th className="text-right px-3 py-2">Opex</th>
              <th className="text-right px-3 py-2">Net Profit</th>
              <th className="text-right px-3 py-2">Closing Cash</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono-num">
            {rows.map((r) => (
              <tr key={r.month} className="hover:bg-white/[0.02]" data-testid={`cf-row-${r.month}`}>
                <td className="px-3 py-1.5 font-display font-black text-[#FBBF24]">M{r.month}</td>
                <td className="px-3 py-1.5 text-right text-white/70">{PESO(r.opening_cash)}</td>
                <td className="px-3 py-1.5 text-right text-[#93C5FD]">{PESO(r.kit_sales)}</td>
                <td className="px-3 py-1.5 text-right text-[#93C5FD]">{PESO(r.product_sales)}</td>
                <td className="px-3 py-1.5 text-right text-white/60">{PESO(r.cogs)}</td>
                <td className="px-3 py-1.5 text-right text-[#FCA5A5]">{PESO(r.commission)}</td>
                <td className="px-3 py-1.5 text-right text-white/60">{PESO(r.opex)}</td>
                <td className={`px-3 py-1.5 text-right font-bold ${r.net_profit >= 0 ? "text-emerald-300" : "text-[#FCA5A5]"}`}>
                  {r.net_profit >= 0 ? <ArrowUpRight size={11} className="inline" /> : <ArrowDownRight size={11} className="inline" />} {PESO(r.net_profit)}
                </td>
                <td className="px-3 py-1.5 text-right text-[#FBBF24] font-bold">{PESO(r.closing_cash)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Profitability & Break-even                                          */
/* ------------------------------------------------------------------ */
function Profitability({ snap }) {
  return (
    <div className="grid gap-5 md:grid-cols-2" data-testid="intel-profitability">
      <div className="glass-card gold-border p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">Break-even Analysis</div>
        <BigStat label="Break-even Revenue (monthly)" value={PESO(snap.break_even_revenue)} accent="gold" />
        <BigStat label="Break-even Units (monthly)" value={snap.break_even_units.toLocaleString()} accent="white" />
        <BigStat label="Break-even Orders" value={snap.break_even_orders.toLocaleString()} accent="white" />
        <BigStat label="Break-even Members" value={snap.break_even_members.toLocaleString()} accent="white" />
        <BigStat
          label="Break-even Month"
          value={snap.break_even_month === null ? "N/A (zero growth)" : snap.break_even_month === 0 ? "Already profitable" : `Month ${snap.break_even_month}`}
          accent="emerald"
        />
        <BigStat label="Margin of Safety" value={PCT(snap.margin_of_safety_pct)} accent="emerald" />
      </div>
      <div className="glass-card p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">Profitability Detail</div>
        <BigStat label="Gross Margin" value={PCT(snap.gross_margin_pct)} accent="gold" />
        <BigStat label="Operating Margin" value={PCT(snap.operating_margin_pct)} accent="gold" />
        <BigStat label="Net Margin" value={PCT(snap.net_margin_pct)} accent="emerald" />
        <BigStat label="Commission Ratio" value={PCT(snap.commission_ratio_pct)} accent="crimson" />
        <BigStat label="Company Retained Margin" value={PESO(snap.company_retained_margin)} accent="white" />
        <BigStat label="LTV" value={PESO(snap.ltv)} accent="blue" />
        <BigStat label="CAC" value={PESO(snap.cac)} accent="white" />
        <BigStat label="LTV : CAC" value={`${snap.ltv_cac_ratio.toFixed(2)}×`} accent="emerald" />
      </div>
    </div>
  );
}

function BigStat({ label, value, accent = "white" }) {
  const map = {
    gold: "text-[#FBBF24]", emerald: "text-emerald-300", crimson: "text-[#FCA5A5]",
    blue: "text-[#93C5FD]", white: "text-white",
  };
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <div className="text-sm text-white/70">{label}</div>
      <div className={`font-mono-num text-lg font-bold ${map[accent]}`}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Scenario Manager                                                    */
/* ------------------------------------------------------------------ */
function ScenarioManager({ scenarios, onLoad, onDelete, onSave, onRefresh }) {
  return (
    <div className="glass-card p-6" data-testid="intel-scenarios">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold">Saved Scenarios</div>
          <div className="text-sm text-white/60">Snapshot the current assumption bundle so you can reload it later.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={onRefresh} className="text-xs px-3 py-2 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1" data-testid="sm-refresh"><RefreshCw size={12} /> Refresh</button>
          <button onClick={onSave} className="text-xs px-3 py-2 bg-[#FBBF24] text-[#0F2042] rounded font-bold hover:bg-yellow-300 inline-flex items-center gap-1" data-testid="sm-save-current"><Save size={12} /> Save Current</button>
        </div>
      </div>
      {scenarios.length === 0 ? (
        <div className="text-white/50 py-10 text-center" data-testid="sm-empty">
          No saved scenarios yet. Adjust the Financial Simulator, then click &ldquo;Save as Scenario&rdquo; to snapshot it.
        </div>
      ) : (
        <div className="grid gap-3">
          {scenarios.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-3 border border-white/10 rounded-md hover:border-[#FBBF24]/40 hover:bg-white/[0.02]" data-testid={`sm-row-${s.id}`}>
              <div>
                <div className="font-display font-black">{s.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/45">{s.tag} · Updated {s.updated_at?.slice(0, 16)?.replace("T", " ")}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => onLoad(s.id)} className="text-xs px-3 py-1.5 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24]" data-testid={`sm-load-${s.id}`}>Load</button>
                <button onClick={() => onDelete(s.id)} className="text-xs px-3 py-1.5 border border-[#DC2626]/40 text-[#FCA5A5] rounded hover:bg-[#DC2626] hover:text-white inline-flex items-center gap-1" data-testid={`sm-delete-${s.id}`}><Trash2 size={11} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Scenario Comparison                                                 */
/* ------------------------------------------------------------------ */
function ScenarioCompare({ scenarios, selectedIds, setSelectedIds, data, onRun }) {
  const toggle = (id) => setSelectedIds(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  const metrics = [
    { key: "monthly_revenue", label: "Monthly Revenue", format: PESO },
    { key: "monthly_gross_profit", label: "Gross Profit", format: PESO },
    { key: "monthly_ebitda", label: "EBITDA", format: PESO },
    { key: "monthly_net_profit", label: "Net Profit", format: PESO },
    { key: "gross_margin_pct", label: "Gross Margin %", format: PCT },
    { key: "operating_margin_pct", label: "Operating Margin %", format: PCT },
    { key: "commission_ratio_pct", label: "Commission Ratio %", format: PCT },
    { key: "cash_position_12mo", label: "Cash @ 12 months", format: PESO },
    { key: "runway_months", label: "Runway (months)", format: (n) => (n >= 999 ? "∞" : n.toFixed(1)) },
    { key: "monthly_commission", label: "Monthly Commission", format: PESO },
    { key: "active_members", label: "Active Members", format: (n) => n.toLocaleString() },
    { key: "break_even_revenue", label: "Break-even Revenue", format: PESO },
    { key: "ltv_cac_ratio", label: "LTV : CAC", format: (n) => `${n.toFixed(2)}×` },
  ];
  const bestPerMetric = useMemo(() => {
    if (!data || data.length === 0) return {};
    const map = {};
    for (const m of metrics) {
      const lowerBetter = m.key === "commission_ratio_pct";
      let best = null;
      let bestVal = lowerBetter ? Infinity : -Infinity;
      for (const s of data) {
        const v = s.summary[m.key] ?? 0;
        if (lowerBetter ? v < bestVal : v > bestVal) { bestVal = v; best = s.id; }
      }
      map[m.key] = best;
    }
    return map;
  }, [data]);
  return (
    <div className="space-y-5" data-testid="intel-compare">
      <div className="glass-card p-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">Pick 2+ Scenarios to Compare</div>
        {scenarios.length === 0 ? (
          <div className="text-white/50 text-center py-6">Save at least 2 scenarios first (Scenario Manager tab).</div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {scenarios.map((s) => (
              <label key={s.id} className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer ${selectedIds.includes(s.id) ? "border-[#FBBF24] bg-[#FBBF24]/8" : "border-white/10 hover:border-white/30"}`} data-testid={`compare-pick-${s.id}`}>
                <input type="checkbox" checked={selectedIds.includes(s.id)} onChange={() => toggle(s.id)} />
                <div>
                  <div className="text-sm font-bold">{s.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{s.tag}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="mt-4 text-right">
          <button onClick={onRun} disabled={selectedIds.length < 2} className="btn-deploy disabled:opacity-50" data-testid="compare-run">
            Compare {selectedIds.length} scenario{selectedIds.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>

      {data && data.length > 0 && (
        <div className="glass-card p-0 overflow-hidden" data-testid="compare-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-[10px] uppercase tracking-[0.16em] text-white/50">
                <tr>
                  <th className="text-left px-4 py-3">Metric</th>
                  {data.map((s) => (
                    <th key={s.id} className="text-right px-4 py-3 font-display text-[#FBBF24]">{s.name}<div className="text-[9px] text-white/40 normal-case tracking-normal">{s.tag}</div></th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {metrics.map((m) => (
                  <tr key={m.key}>
                    <td className="px-4 py-2.5 text-white/80">{m.label}</td>
                    {data.map((s) => {
                      const v = s.summary[m.key];
                      const best = bestPerMetric[m.key] === s.id;
                      return (
                        <td key={s.id} className={`px-4 py-2.5 text-right font-mono-num ${best ? "text-emerald-300 font-bold" : "text-white/75"}`}>
                          {m.format(v ?? 0)} {best && <span className="text-[9px] uppercase tracking-widest text-emerald-400/70 ml-1">best</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Phase 2 Roadmap                                                     */
/* ------------------------------------------------------------------ */
function Roadmap() {
  const items = [
    { title: "Compensation Simulator", body: "Deep-dive on retail margin, override tiers, leadership/matching pools with instant recalc." },
    { title: "Inventory Planning", body: "Products required, safety stock, PO recommendations, warehouse utilization + auto reorder alerts. Needs live inventory feed." },
    { title: "Marketing Simulator", body: "Split spend across Facebook/TikTok/Google/Influencer/Referral, compute CAC/ROAS/LTV:CAC per channel." },
    { title: "Sensitivity Analysis", body: "Adjust one variable (cost, commission, SRP) and see impact on all KPIs instantly with waterfall visualisation." },
    { title: "AI CFO", body: "Claude Sonnet 4.5 analyses assumptions, flags target breaches, and proposes priority-ordered corrective actions." },
    { title: "Investor Presentation Mode", body: "Fullscreen, arrow-key navigation, auto-counting numbers, executive summary + growth story deck." },
    { title: "Export Center", body: "PDF · Excel · CSV · PowerPoint-ready charts · print-friendly reports." },
    { title: "Live Data Integrations", body: "Connect real orders, payments, inventory, ad platforms — toggle Actual Performance vs Forecast overlay." },
  ];
  return (
    <div className="glass-card p-6" data-testid="intel-roadmap">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">Phase 2 Submodules</div>
      <div className="text-white/70 text-sm mb-6 max-w-3xl">
        The MVP shipped in Phase 1 covers Executive Dashboard, Financial Simulator, Cash Flow, 5-Year Forecast,
        Break-even & Profitability, Scenario Manager, and Scenario Comparison. The submodules below are the
        next tranche. Un-park them when you&apos;re ready — the calc engine already exposes the metrics they need.
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((r) => (
          <div key={r.title} className="border border-white/10 rounded-md p-4 hover:border-[#FBBF24]/40">
            <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold">Phase 2</div>
            <div className="font-display font-black mt-1">{r.title}</div>
            <div className="text-xs text-white/60 leading-relaxed mt-1">{r.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
